import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  SetStateAction,
  useMemo,
  useEffect,
} from "react";
import { isEqual, isFunction } from "lodash";
import {
  SellDialogType,
  MergeDialogType,
  UpgradeDialogType,
  InstantHealDialogType,
} from "../../types/pages/Barracks";
import { TroopItemsDialogStateType } from "../../types/troop";
import { CampInfo } from "../../game-engine/types";
import { useSearchParams } from "react-router-dom";
import { ConfirmDialogType } from "../../types/global";

const initialDialogs: ConfirmDialogType = {
  open: false,
  submit: null,
  id: null,
  title: "",
  msg: "",
};

const initialUpgradeDialogs: UpgradeDialogType = {
  open: false,
  id: 0,
  cost: {
    clegCost: 0,
    stoneCost: 0,
    ironCost: 0,
    lastClegCost: 0,
    lastStoneCost: 0,
    lastIronCost: 0,

    clegDiscount: 0,
  },
};

const initialTroopItem: TroopItemsDialogStateType = {
  open: false,
  troop: undefined,
};

const initialInstantHealDialog: InstantHealDialogType = {
  open: false,
  troopId: 0,
  troop: undefined,
  targetType: "all",
};

export interface BarracksContextInterface {
  confirmDialog: ConfirmDialogType;
  setConfirmDialog: (
    nextConfirmDialog: SetStateAction<ConfirmDialogType>
  ) => void;
  mergeDialog: MergeDialogType;
  setMergeDialog: (nextMergeDialog: SetStateAction<MergeDialogType>) => void;
  sellDialog: SellDialogType;
  setSellDialog: (nextSellDialog: SetStateAction<SellDialogType>) => void;
  upgradeDialog: UpgradeDialogType;
  setUpgradeDialog: (
    nextUpgradeDialog: SetStateAction<UpgradeDialogType>
  ) => void;
  troopItemsDialog: TroopItemsDialogStateType;
  setTroopItemsDialog: (
    nextTroopItemsDialog: SetStateAction<TroopItemsDialogStateType>
  ) => void;
  instantHealDialog: InstantHealDialogType;
  setInstantHealDialog: (
    nextInstantHealDialog: SetStateAction<InstantHealDialogType>
  ) => void;
  campInfo: CampInfo |undefined;
  setCampInfo: (nextCampInfo: SetStateAction<CampInfo>) => void;
  trainDialog: boolean;
  setTrainDialog: (nextTrainDialog: SetStateAction<boolean>) => void;
  upgradeLoading: boolean;
  setUpgradeLoading: (nextUpgradeLoading: SetStateAction<boolean>) => void;
  selectedTab: "items" | "camp" | undefined;
  setSelectedTab: (
    nextSelectedTab: SetStateAction<"items" | "camp" | undefined>
  ) => void;
}

interface BarracksUiContextInterface {
  children: React.ReactNode;
}

const BarracksUIContext = createContext<BarracksContextInterface | null>(null);
const BarracksUIConsumer = BarracksUIContext.Consumer;

const useBarracksUIContext = () => {
  return useContext<BarracksContextInterface | null>(BarracksUIContext);
};

const BarracksUIProvider: React.FC<BarracksUiContextInterface> = ({
  children,
}) => {
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get("type");
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogType>(
    initialDialogs as ConfirmDialogType
  );
  const [mergeDialog, setMergeDialog] = useState<MergeDialogType>(
    initialDialogs as MergeDialogType
  );
  const [sellDialog, setSellDialog] = useState<SellDialogType>(
    initialDialogs as SellDialogType
  );
  const [upgradeDialog, setUpgradeDialog] = useState<UpgradeDialogType>(
    initialUpgradeDialogs as UpgradeDialogType
  );
  const [troopItemsDialog, setTroopItemsDialog] =
    useState<TroopItemsDialogStateType>(
      initialTroopItem as TroopItemsDialogStateType
    );
  const [instantHealDialog, setInstantHealDialog] =
    useState<InstantHealDialogType>(
      initialInstantHealDialog as InstantHealDialogType
    );
  const [upgradeLoading, setUpgradeLoading] = useState<boolean>(false);
  const [trainDialog, setTrainDialog] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<"items"|"camp" | undefined>(
    undefined
  );
  const [campInfo, setCampInfo] = useState<CampInfo>();

  const setConfirmDialogBase = useCallback(
    (nextConfirm: SetStateAction<ConfirmDialogType>) => {
      setConfirmDialog((prevConfirm) => {
        if (isFunction(nextConfirm)) {
          nextConfirm = nextConfirm(prevConfirm);
        }

        if (isEqual(nextConfirm, prevConfirm)) {
          return prevConfirm;
        }

        return nextConfirm;
      });
    },
    []
  );

  const setMergeDialogBase = useCallback(
    (nextMerge: SetStateAction<MergeDialogType>) => {
      setMergeDialog((prevMerge) => {
        if (isFunction(nextMerge)) {
          nextMerge = nextMerge(prevMerge);
        }

        if (isEqual(nextMerge, prevMerge)) {
          return prevMerge;
        }

        return nextMerge;
      });
    },
    []
  );

  const setSellDialogBase = useCallback(
    (nextSell: SetStateAction<SellDialogType>) => {
      setSellDialog((prevSell) => {
        if (isFunction(nextSell)) {
          nextSell = nextSell(prevSell);
        }

        if (isEqual(nextSell, prevSell)) {
          return prevSell;
        }

        return nextSell;
      });
    },
    []
  );

  const setUpgradeDialogBase = useCallback(
    (nextUpgrade: SetStateAction<UpgradeDialogType>) => {
      setUpgradeDialog((prevUpgrade) => {
        if (isFunction(nextUpgrade)) {
          nextUpgrade = nextUpgrade(prevUpgrade);
        }

        if (isEqual(nextUpgrade, prevUpgrade)) {
          return prevUpgrade;
        }

        return nextUpgrade;
      });
    },
    []
  );

  const setTroopItemsDialogBase = useCallback(
    (nextTroopItems: SetStateAction<TroopItemsDialogStateType>) => {
      setTroopItemsDialog((prevTroopItems) => {
        if (isFunction(nextTroopItems)) {
          nextTroopItems = nextTroopItems(prevTroopItems);
        }

        if (isEqual(nextTroopItems, prevTroopItems)) {
          return prevTroopItems;
        }

        return nextTroopItems;
      });
    },
    []
  );

  const setTrainDialogBase = useCallback(
    (nextTrain: SetStateAction<boolean>) => {
      setTrainDialog((prevTrain) => {
        if (isFunction(nextTrain)) {
          nextTrain = nextTrain(prevTrain);
        }

        if (isEqual(nextTrain, prevTrain)) {
          return prevTrain;
        }

        return nextTrain;
      });
    },
    []
  );

  const setInstantHealDialogBase = useCallback(
    (nextInstantHeal: SetStateAction<InstantHealDialogType>) => {
      setInstantHealDialog((prevInstantHeal) => {
        if (isFunction(nextInstantHeal)) {
          nextInstantHeal = nextInstantHeal(prevInstantHeal);
        }

        if (isEqual(nextInstantHeal, prevInstantHeal)) {
          return prevInstantHeal;
        }

        return nextInstantHeal;
      });
    },
    []
  );

  const setSelectedTabBase = useCallback(
    (nextSelectedTab: SetStateAction<"items"|"camp" | undefined>) => {
      setSelectedTab((prevSelectedTab) => {
        if (isFunction(nextSelectedTab)) {
          nextSelectedTab = nextSelectedTab(prevSelectedTab);
        }

        if (isEqual(nextSelectedTab, prevSelectedTab)) {
          return prevSelectedTab;
        }

        return nextSelectedTab;
      });
    },
    []
  );

  const setUpgradeLoadingBase = useCallback(
    (nextUpgradeLoading: SetStateAction<boolean>) => {
      setUpgradeLoading((prevUpgradeLoading) => {
        if (isFunction(nextUpgradeLoading)) {
          nextUpgradeLoading = nextUpgradeLoading(prevUpgradeLoading);
        }

        if (isEqual(nextUpgradeLoading, prevUpgradeLoading)) {
          return prevUpgradeLoading;
        }

        return nextUpgradeLoading;
      });
    },
    []
  );
  useEffect(()=>{
   if(initialType!==selectedTab)   setSelectedTab(initialType as "items"|"camp")
  },[initialType])

  const value: BarracksContextInterface = useMemo(() => {
    return {
      confirmDialog: confirmDialog,
      setConfirmDialog: setConfirmDialogBase,
      mergeDialog: mergeDialog,
      setMergeDialog: setMergeDialogBase,
      sellDialog: sellDialog,
      setSellDialog: setSellDialogBase,
      upgradeLoading: upgradeLoading,
      setUpgradeLoading: setUpgradeLoadingBase,
      selectedTab: selectedTab,
      setSelectedTab: setSelectedTabBase,
      trainDialog: trainDialog,
      setTrainDialog: setTrainDialogBase,
      upgradeDialog: upgradeDialog,
      setUpgradeDialog: setUpgradeDialogBase,
      troopItemsDialog: troopItemsDialog,
      setTroopItemsDialog: setTroopItemsDialogBase,
      instantHealDialog: instantHealDialog,
      setInstantHealDialog: setInstantHealDialogBase,
      campInfo: campInfo,
      setCampInfo: (nextCampInfo: SetStateAction<CampInfo>) => {
        setCampInfo(nextCampInfo as SetStateAction<CampInfo | undefined>);
      }
    };
  }, [
    upgradeLoading,
    setUpgradeLoadingBase,
    confirmDialog,
    setConfirmDialogBase,
    selectedTab,
    setSelectedTabBase,
    trainDialog,
    setTrainDialogBase,
    mergeDialog,
    setMergeDialogBase,
    sellDialog,
    setSellDialogBase,
    upgradeDialog,
    setUpgradeDialogBase,
    troopItemsDialog,
    setTroopItemsDialogBase,
    instantHealDialog,
    setInstantHealDialogBase,
    campInfo,
    setCampInfo,
  ]);

  return (
    <BarracksUIContext.Provider value={value}>
      {children}
    </BarracksUIContext.Provider>
  );
};
export { BarracksUIProvider, BarracksUIConsumer, useBarracksUIContext };
